import type { AppVersion } from '@konyaxl/shared';
import { useEffect, useState, type ReactNode } from 'react';
import { isNative } from '../core/native.js';
import {
  checkForUpdate,
  reloadWebAssets,
  startPeriodicCheck,
  startUpdateApk,
  type UpdateState,
} from '../core/updater.js';

/**
 * Güncelleme kapısı.
 *
 * Uygulamayı sarar. Zorunlu bir APK güncellemesi varsa içeriği göstermez —
 * kullanıcı güncellemeden devam edemez. Arayüz güncellemesi ise sessizce
 * uygulanır, kullanıcı fark etmez.
 */
export function UpdateGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<UpdateState | null>(null);

  useEffect(() => {
    let iptal = false;

    (async () => {
      const ilk = await checkForUpdate();
      if (iptal) return;

      if (ilk.action === 'reload' && ilk.server) {
        reloadWebAssets(ilk.server.webBuild);
        return;
      }

      setState(ilk);
    })();

    const durdur = startPeriodicCheck((yeni) => {
      if (iptal) return;

      /**
       * Arayüz güncellemesi vardiya ortasında sayfayı yenilemez: operatör
       * tarama yaparken ekranın sıfırlanması iş kaybına yol açar. Bir sonraki
       * açılışta uygulanır. APK güncellemesi ise hemen gösterilir.
       */
      if (yeni.action === 'apk-required' || yeni.action === 'apk-optional') {
        setState(yeni);
      }
    });

    return () => {
      iptal = true;
      durdur();
    };
  }, []);

  if (state?.action === 'apk-required' && state.server) {
    return <UpdateScreen server={state.server} zorunlu />;
  }

  if (state?.action === 'apk-optional' && state.server) {
    return (
      <>
        <UpdateBanner
          server={state.server}
          onDismiss={() => setState({ action: 'none', server: null })}
        />
        {children}
      </>
    );
  }

  return <>{children}</>;
}

function boyutMB(bytes: number | null): string {
  if (!bytes) return '';
  return ` · ${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function UpdateScreen({ server, zorunlu }: { server: AppVersion; zorunlu: boolean }) {
  const [durum, setDurum] = useState<'bekliyor' | 'indiriliyor' | 'hata'>('bekliyor');
  const [yuzde, setYuzde] = useState(0);
  const [hata, setHata] = useState<string | null>(null);

  async function guncelle() {
    setDurum('indiriliyor');
    setHata(null);
    setYuzde(0);

    try {
      await startUpdateApk(server, setYuzde);
      // Kurulum ekranı Android tarafından açılır; uygulama burada bekler.
    } catch (err) {
      setDurum('hata');
      setHata(err instanceof Error ? err.message : 'Güncelleme başarısız oldu');
    }
  }

  return (
    <div className="guncelleme-sayfa">
      <div className="guncelleme-kart">
        <div className="guncelleme-ikon">↑</div>

        <h1>Yeni sürüm mevcut</h1>
        <p className="guncelleme-surum">
          {server.versionName}
          {boyutMB(server.apkBytes)}
        </p>

        {server.notes && <div className="guncelleme-notlar">{server.notes}</div>}

        {zorunlu && (
          <p className="guncelleme-uyari">
            Devam etmek için uygulamayı güncellemeniz gerekiyor.
          </p>
        )}

        {hata && <div className="hata-kutu">{hata}</div>}

        {durum === 'indiriliyor' ? (
          <div className="ilerleme">
            <div className="ilerleme-cubuk">
              <div className="ilerleme-dolgu" style={{ width: `${yuzde}%` }} />
            </div>
            <span className="ilerleme-yazi">
              {yuzde > 0 ? `İndiriliyor… %${Math.round(yuzde)}` : 'Hazırlanıyor…'}
            </span>
          </div>
        ) : (
          <button className="btn-birincil" onClick={() => void guncelle()}>
            {durum === 'hata' ? 'Tekrar dene' : 'Güncelle'}
          </button>
        )}

        {!isNative() && (
          <p className="guncelleme-ipucu">
            Bu ekran tarayıcıda gösteriliyor. Kurulum yalnızca Android
            uygulamasında yapılabilir.
          </p>
        )}
      </div>
    </div>
  );
}

function UpdateBanner({
  server,
  onDismiss,
}: {
  server: AppVersion;
  onDismiss: () => void;
}) {
  const [acik, setAcik] = useState(false);

  if (acik) return <UpdateScreen server={server} zorunlu={false} />;

  return (
    <div className="guncelleme-serit">
      <span>
        Yeni sürüm hazır: <strong>{server.versionName}</strong>
      </span>
      <button className="btn-serit" onClick={() => setAcik(true)}>
        Güncelle
      </button>
      <button className="btn-serit sade" onClick={onDismiss} aria-label="Kapat">
        ✕
      </button>
    </div>
  );
}
